import { API } from 'global/api'
import { CONSTANT } from 'global/constant'
import type { NotificationResponse, PaginatedResponse } from 'global/types/responses'
import { api, headers } from './api'
import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { type RootState } from './store'
import { uniqBy } from 'shared/services/utils'
export interface NotificationsQueryParams {
  start?: number
  length?: number
}

const notificationsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getNotifications: builder.query<
      PaginatedResponse<NotificationResponse>,
      NotificationsQueryParams
    >({
      query: ({ start = 0, length = 10 }) => ({
        url: API.NOTIFICATIONS_URL,
        method: 'GET',
        headers: headers(),
        params: { start, length }
      }),
      providesTags: [CONSTANT.NOTIFICATION_TAG]
    }),
    getNotificationDetail: builder.query<NotificationResponse, number>({
      query: (id) => ({
        url: API.NOTIFICATION_DETAIL_URL.replace('{id}', String(id)),
        method: 'GET',
        headers: headers()
      }),
      providesTags: (result, error, id) => [{ type: CONSTANT.NOTIFICATION_TAG, id }]
    }),
    updateIsReadNotification: builder.mutation<void, number>({
      query: (id) => ({
        url: API.NOTIFICATION_READ_URL.replace('{id}', String(id)),
        method: 'PATCH',
        headers: headers()
      }),
      invalidatesTags: (result, error) => [{ type: CONSTANT.NOTIFICATION_TAG }]
    })
  })
})

interface NotificationState {
  notifications: NotificationResponse[]
  currentPage: number
  totalRecords: number
}

const initialState: NotificationState = {
  notifications: [],
  currentPage: 0,
  totalRecords: 0
}

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setNotifications: (state, action: PayloadAction<NotificationResponse[]>) => {
      state.notifications = action.payload
      state.currentPage = 0
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload
    },
    appendNotifications: (state, action: PayloadAction<NotificationResponse[]>) => {
      state.notifications = uniqBy([...state.notifications, ...action.payload], 'pkId')
    },
    clearNotifications: (state) => {
      state.notifications = []
      state.currentPage = 0
    },
    setTotalRecords: (state, action: PayloadAction<number>) => {
      state.totalRecords = action.payload
    }
  }
})

export const notificationsReducer = notificationsSlice.reducer

export const {
  setNotifications,
  appendNotifications,
  setCurrentPage,
  clearNotifications,
  setTotalRecords
} = notificationsSlice.actions

// notifs selector
export const notificationsSelector = (state: RootState) => state.notifications

export const {
  useGetNotificationsQuery,
  useGetNotificationDetailQuery,
  useUpdateIsReadNotificationMutation
} = notificationsApi
